import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { signIn, signOut } from 'aws-amplify/auth';
import { api } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { Logo } from '../components/Logo';
import { SUPPORT_EMAIL } from '../config/support';
import type { PublicDeviceStatus } from '../types';
import './ProvisionWizard.css';

interface ProvisionWizardProps {
  serialNumber?: string;
  publicStatus?: PublicDeviceStatus;
}

interface AdoptResult {
  deviceUuid: string;
  serialNumber: string;
  bindingId: string;
  siteId: string;
  lifecycleStatus: 'adopted';
  nodeId: number;
  role: 'leaf' | 'cellular' | 'gateway';
}

const siteId = (site: Record<string, unknown>): string => String(
  site.siteId || site.id || (site.PK ? String(site.PK).replace('SITE#', '') : ''),
);

/** Shared login-gated adoption wizard used by the permanent `/d/:serial` QR landing. */
export function ProvisionWizard({ serialNumber: serialProp, publicStatus: initialStatus }: ProvisionWizardProps) {
  const { provisioningId } = useParams<{ provisioningId: string }>();
  const serialNumber = serialProp ?? provisioningId ?? '';
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, checkAuth } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [status, setStatus] = useState<PublicDeviceStatus | null>(initialStatus ?? null);
  const [loadError, setLoadError] = useState('');
  const [sites, setSites] = useState<Record<string, unknown>[]>([]);
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [selectedSite, setSelectedSite] = useState('');
  const [nodeName, setNodeName] = useState('');
  const [tankShape, setTankShape] = useState('cylindrical_vertical');
  const [diameter, setDiameter] = useState('');
  const [height, setHeight] = useState('');
  const [mountHeight, setMountHeight] = useState('');
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<AdoptResult | null>(null);

  useEffect(() => {
    if (initialStatus || !serialNumber) return;
    api.get(`/api/v1/devices/${encodeURIComponent(serialNumber)}/public-status`)
      .then(setStatus)
      .catch((error) => setLoadError(error instanceof Error ? error.message : 'Could not load this device'));
  }, [initialStatus, serialNumber]);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get('/api/v1/sites', { headers: { 'X-Tenant-ID': '' } })
      .then((rows) => { setSites(rows || []); setSitesLoaded(true); })
      .catch((error) => setLoadError(error instanceof Error ? error.message : 'Could not load your sites'));
  }, [isAuthenticated]);

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    setAuthError('');
    setAuthBusy(true);
    try {
      try { await signOut(); } catch { /* clear an incomplete session */ }
      const loginResult = await signIn({ username: email, password });
      if (loginResult.isSignedIn) {
        await checkAuth();
      } else {
        setAuthError('Additional sign-in steps are required. Finish signing in on the main login page, then reopen this QR link.');
      }
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Sign in failed');
    } finally {
      setAuthBusy(false);
    }
  };

  const handleAdopt = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    setSubmitBusy(true);
    try {
      const body: Record<string, unknown> = {
        siteId: selectedSite,
        nodeName,
        unitType: 'MobileStation',
        tankShape,
        tankDimensions: { diameter: Number(diameter) || 0, height: Number(height) || 0 },
        sensorMountHeight: Number(mountHeight) || 0,
      };
      const adopted = await api.post(`/api/v1/devices/${encodeURIComponent(serialNumber)}/adopt`, body);
      localStorage.setItem(`rtm-device-uuid:${serialNumber}`, adopted.deviceUuid);
      setResult(adopted);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to add the device');
    } finally {
      setSubmitBusy(false);
    }
  };

  if (isLoading || (!status && !loadError)) return <div className="pw-shell"><p>Loading device…</p></div>;
  if (loadError) return <div className="pw-shell"><div className="pw-card"><Logo /><h1>Device unavailable</h1><p className="pw-error">{loadError}</p></div></div>;
  if (!status?.adoptable) return <div className="pw-shell"><div className="pw-card"><Logo /><h1>Device is not adoptable</h1><p className="pw-sub">Its current lifecycle state is {status?.lifecycleStatus ?? 'unknown'}.</p></div></div>;

  if (!isAuthenticated) {
    return (
      <div className="pw-shell"><div className="pw-card">
        <Logo />
        <h1>Add {serialNumber}</h1>
        <p className="pw-sub">Sign in with an account that administers the installation site.</p>
        <form onSubmit={handleLogin}>
          <label className="pw-field"><span>Email</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label className="pw-field"><span>Password</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
          {authError && <p className="pw-error">{authError}</p>}
          <button className="pw-btn" type="submit" disabled={authBusy}>{authBusy ? 'Signing in…' : 'Sign in'}</button>
        </form>
        <p className="pw-sub" style={{ marginTop: '1rem' }}>
          Don't have an account yet? Ask your site administrator for an invitation, or contact{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </div></div>
    );
  }

  if (result) {
    return (
      <div className="pw-shell"><div className="pw-card">
        <Logo />
        <h1>Device added</h1>
        <p className="pw-sub">{result.serialNumber} is now node #{result.nodeId} ({result.role}) in the selected site.</p>
        <button className="pw-btn" onClick={() => navigate(`/dashboard/sites?siteId=${result.siteId}`)}>Go to site</button>
      </div></div>
    );
  }

  return (
    <div className="pw-shell"><div className="pw-card pw-card--wide">
      <Logo />
      <h1>Add this device</h1>
      <p className="pw-sub">Serial {serialNumber} · {status.lifecycleStatus}</p>
      <form onSubmit={handleAdopt}>
        {sitesLoaded && sites.length === 0 && (
          <p className="pw-error">
            Your account does not have access to any sites yet. Ask your site administrator for an
            invitation, or contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> to get set up.
          </p>
        )}
        <label className="pw-field"><span>Site</span>
          <select value={selectedSite} onChange={(event) => setSelectedSite(event.target.value)} required>
            <option value="">Choose a site…</option>
            {sites.map((site) => <option key={siteId(site)} value={siteId(site)}>{String(site.name || siteId(site))}</option>)}
          </select>
        </label>
        <label className="pw-field"><span>Name</span><input value={nodeName} onChange={(event) => setNodeName(event.target.value)} placeholder="e.g. North Tank" required /></label>
        <fieldset className="pw-fieldset">
          <legend>Tank</legend>
          <label className="pw-field"><span>Shape</span>
            <select value={tankShape} onChange={(event) => setTankShape(event.target.value)}>
              <option value="cylindrical_vertical">Cylindrical vertical</option>
              <option value="cylindrical_horizontal">Cylindrical horizontal</option>
            </select>
          </label>
          <div className="pw-row">
            <label className="pw-field"><span>Diameter (cm)</span><input type="number" value={diameter} onChange={(event) => setDiameter(event.target.value)} /></label>
            <label className="pw-field"><span>Height/length (cm)</span><input type="number" value={height} onChange={(event) => setHeight(event.target.value)} /></label>
            <label className="pw-field"><span>Mount height (cm)</span><input type="number" min={1} value={mountHeight} onChange={(event) => setMountHeight(event.target.value)} /></label>
          </div>
          <p className="pw-hint">
            Mount height is how far the sensor sits <strong>above the top of the tank</strong> — enter 0
            if it is mounted level with the rim.
            {Number(height) > 0 && (
              <> A full tank then reads {Number(mountHeight) || 0} cm and an empty one
              reads {Number(height) + (Number(mountHeight) || 0)} cm.</>
            )}
          </p>
        </fieldset>
        {submitError && <p className="pw-error">{submitError}</p>}
        <button
          className="pw-btn"
          type="submit"
          disabled={submitBusy || (sitesLoaded && sites.length === 0)}
          title={sitesLoaded && sites.length === 0 ? 'You need access to at least one site before adding a device.' : undefined}
        >{submitBusy ? 'Adding…' : 'Add device to site'}</button>
      </form>
    </div></div>
  );
}

export default ProvisionWizard;
